import type { SupabaseClient } from '@supabase/supabase-js'

export type GraphTool = {
  name: string
  writeScoped: boolean
  inputSchema: Record<string, unknown>
  run: (args: Record<string, unknown>, ctx: GraphToolContext) => Promise<unknown>
}

export type GraphToolContext = {
  workspaceId: string
  userId?: string | null
  brandId: string | null
  runId?: string | null
  attachmentIds?: string[]
  admin: SupabaseClient
  sandbox: boolean
}

type CampaignRow = {
  id: string
  name: string | null
  budget: number | string | null
  start_date?: string | null
  end_date?: string | null
  form_state?: unknown
}

type PerfRow = {
  campaign_id: string
  impressions: number | string | null
  clicks: number | string | null
  spend_cents: number | string | null
}

export type OutputFormat = 'text' | 'markdown' | 'json' | 'csv'

type FormatOutputOptions = {
  csv_delimiter?: string
  json_indent?: number
  markdown_style?: 'gfm' | 'plain'
}

export type FormatOutputResult = {
  formatted: string
  format: OutputFormat
  byteSize: number
}

type ChatAttachmentRow = {
  id: string
  workspace_id: string
  user_id: string
  file_name: string
  file_type: string
  file_size: number | string
  storage_path: string
}

const CHAT_ATTACHMENTS_BUCKET = 'chat-uploads'
const DEFAULT_READ_MAX_BYTES = 500_000
const HARD_READ_MAX_BYTES = 5_000_000

export class AttachmentNotFoundError extends Error {
  constructor(message = 'Attachment not found') {
    super(message)
    this.name = 'AttachmentNotFoundError'
  }
}

export class AttachmentNotAllowedError extends Error {
  constructor(message = 'Attachment is not available to this command run') {
    super(message)
    this.name = 'AttachmentNotAllowedError'
  }
}

export const graphTools: GraphTool[] = [
  {
    name: 'read_file_attachment',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: {
        attachment_id: { type: 'string' },
        mode: { enum: ['text', 'json', 'metadata_only'] },
        max_bytes: { type: 'number' },
      },
      required: ['attachment_id', 'mode'],
    },
    run: async (args, ctx) => {
      const attachmentId = requireString(args.attachment_id, 'attachment_id')
      const mode = parseReadMode(args.mode)
      const maxBytes = parseMaxBytes(args.max_bytes)
      const attachment = await loadAttachment(ctx, attachmentId)
      const byteSize = Number(attachment.file_size ?? 0)

      if (mode === 'metadata_only') {
        return attachmentResult(attachment, null, false)
      }

      const buffer = await downloadAttachmentBytes(ctx, attachment.storage_path)
      const truncated = byteSize > maxBytes || buffer.byteLength > maxBytes
      const text = buffer.subarray(0, maxBytes).toString('utf8')

      if (mode === 'json') {
        try {
          return attachmentResult(attachment, JSON.stringify(JSON.parse(text)), truncated)
        } catch {
          throw new Error(`Attachment ${attachmentId} is not valid JSON`)
        }
      }

      return attachmentResult(attachment, text, truncated)
    },
  },
  {
    name: 'write_file_output',
    writeScoped: true,
    inputSchema: {
      type: 'object',
      properties: {
        content: { type: 'string' },
        filename: { type: 'string' },
        mime_type: { type: 'string' },
      },
      required: ['content', 'filename'],
    },
    run: async (args, ctx) => {
      const content = requireString(args.content, 'content')
      const originalFilename = requireString(args.filename, 'filename')
      const filename = sanitizeStorageFilename(originalFilename)
      const mimeType = typeof args.mime_type === 'string' && args.mime_type.trim()
        ? args.mime_type.trim()
        : inferMimeType(filename)
      const runId = ctx.runId
      if (!runId) throw new Error('write_file_output requires a command run id')
      if (!ctx.userId) throw new Error('write_file_output requires a user id')

      const bytes = Buffer.from(content, 'utf8')
      const storagePath = `commands/${ctx.workspaceId}/${runId}/${filename}`
      const { error: uploadError } = await ctx.admin.storage
        .from(CHAT_ATTACHMENTS_BUCKET)
        .upload(storagePath, bytes, { contentType: mimeType, upsert: false })
      if (uploadError) throw new Error(`write_file_output upload failed: ${uploadError.message}`)

      const { data: attachment, error: insertError } = await (ctx.admin as any)
        .from('chat_attachments')
        .insert({
          workspace_id: ctx.workspaceId,
          user_id: ctx.userId,
          file_name: originalFilename,
          file_type: mimeType,
          file_size: bytes.byteLength,
          storage_path: storagePath,
          command_run_id: runId,
          metadata: { kind: 'command_output', command_run_id: runId },
        })
        .select('id')
        .single()

      if (insertError || !attachment) {
        await ctx.admin.storage.from(CHAT_ATTACHMENTS_BUCKET).remove([storagePath])
        throw new Error(`write_file_output insert failed: ${insertError?.message || 'missing attachment row'}`)
      }

      const { data: signed, error: signedError } = await ctx.admin.storage
        .from(CHAT_ATTACHMENTS_BUCKET)
        .createSignedUrl(storagePath, 60 * 60)
      if (signedError || !signed?.signedUrl) {
        throw new Error(`write_file_output signed URL failed: ${signedError?.message || 'missing signed URL'}`)
      }

      return {
        attachment_id: String((attachment as { id: string }).id),
        filename: originalFilename,
        mime_type: mimeType,
        byte_size: bytes.byteLength,
        download_url: signed.signedUrl,
      }
    },
  },
  {
    name: 'get_campaign_performance',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: {
        period: { enum: ['today', 'yesterday', 'last_7', 'last_30'] },
        campaignId: { type: 'string' },
      },
    },
    run: async (args, ctx) => {
      const period = parsePeriod(args.period)
      const campaignId = typeof args.campaignId === 'string' && args.campaignId ? args.campaignId : null
      const campaigns = await loadCampaigns(ctx, campaignId)
      const totals = await loadPerformance(ctx, campaigns.map((campaign) => campaign.id), period)

      return {
        campaigns: campaigns.map((campaign) => {
          const metrics = totals.get(campaign.id) ?? { spend_cents: 0, impressions: 0, clicks: 0 }
          const budgetCents = budgetCentsForCampaign(campaign)
          return {
            id: campaign.id,
            name: campaign.name ?? 'Untitled campaign',
            spend_cents: metrics.spend_cents,
            budget_cents: budgetCents,
            spend_pct: budgetCents > 0 ? metrics.spend_cents / budgetCents : 0,
            impressions: metrics.impressions,
            clicks: metrics.clicks,
          }
        }),
      }
    },
  },
  {
    name: 'get_creative_status',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: { campaignId: { type: 'string' } },
      required: ['campaignId'],
    },
    run: async (args, ctx) => {
      const campaignId = requireString(args.campaignId, 'campaignId')
      const campaigns = await loadCampaigns(ctx, campaignId)
      if (campaigns.length === 0) return { creatives: [] }

      const { data: attachments, error: attachmentError } = await (ctx.admin as any)
        .from('creative_campaign_attachments')
        .select('creative_id, attach_status, error_message')
        .eq('campaign_id', campaignId)

      if (attachmentError) throw new Error(`get_creative_status failed: ${attachmentError.message}`)
      const creativeIds = Array.from(new Set(((attachments ?? []) as Array<{ creative_id: string }>).map((row) => row.creative_id)))
      if (creativeIds.length === 0) return { creatives: [] }

      const { data: creatives, error: creativeError } = await (ctx.admin as any)
        .from('creative_assets')
        .select('id, display_name, status')
        .eq('workspace_id', ctx.workspaceId)
        .in('id', creativeIds)

      if (creativeError) throw new Error(`get_creative_status failed: ${creativeError.message}`)
      const attachmentByCreative = new Map(
        ((attachments ?? []) as Array<{ creative_id: string; attach_status: string | null; error_message: string | null }>)
          .map((row) => [row.creative_id, row]),
      )

      return {
        creatives: ((creatives ?? []) as Array<{ id: string; display_name: string | null; status: string | null }>).map((creative) => {
          const attachment = attachmentByCreative.get(creative.id)
          return {
            id: creative.id,
            name: creative.display_name ?? 'Untitled creative',
            status: attachment?.attach_status ?? creative.status ?? 'unknown',
            rejection_reason: attachment?.error_message ?? undefined,
          }
        }),
      }
    },
  },
  {
    name: 'get_budget_status',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: { campaignId: { type: 'string' } },
    },
    run: async (args, ctx) => {
      const campaignId = typeof args.campaignId === 'string' && args.campaignId ? args.campaignId : null
      const campaigns = await loadCampaigns(ctx, campaignId)
      const today = dateOnly(new Date())
      const totals = await loadPerformance(ctx, campaigns.map((campaign) => campaign.id), { start: today, end: today })

      return {
        campaigns: campaigns.map((campaign) => {
          const dailyBudget = dailyBudgetCentsForCampaign(campaign)
          const spendToday = totals.get(campaign.id)?.spend_cents ?? 0
          return {
            id: campaign.id,
            name: campaign.name ?? 'Untitled campaign',
            daily_budget: dailyBudget,
            spend_today: spendToday,
            pct_used: dailyBudget > 0 ? spendToday / dailyBudget : 0,
          }
        }),
      }
    },
  },
  {
    name: 'get_recent_command_runs',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: {
        commandId: { type: 'string' },
        limit: { type: 'number' },
      },
    },
    run: async (args, ctx) => {
      const limit = Math.min(Math.max(Number(args.limit ?? 10), 1), 50)
      let query = (ctx.admin as any)
        .from('command_runs')
        .select('id, command_id, status, response, created_at')
        .eq('workspace_id', ctx.workspaceId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (typeof args.commandId === 'string' && args.commandId) query = query.eq('command_id', args.commandId)

      const { data, error } = await query
      if (error) throw new Error(`get_recent_command_runs failed: ${error.message}`)
      return {
        runs: ((data ?? []) as Array<{ id: string; status: string | null; response: string | null; created_at: string }>).map((run) => ({
          id: run.id,
          status: run.status,
          response: run.response,
          created_at: run.created_at,
        })),
      }
    },
  },
  {
    name: 'format_currency',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: { cents: { type: 'number' } },
      required: ['cents'],
    },
    run: async (args) => {
      const cents = Number(args.cents ?? 0)
      return `$${(cents / 100).toFixed(2)}`
    },
  },
  {
    name: 'format_percent',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: {
        ratio: { type: 'number' },
        decimals: { type: 'number' },
      },
      required: ['ratio'],
    },
    run: async (args) => {
      const ratio = Number(args.ratio ?? 0)
      const decimals = Math.min(Math.max(Number(args.decimals ?? 1), 0), 6)
      return `${(ratio * 100).toFixed(decimals)}%`
    },
  },
  {
    name: 'format_output',
    writeScoped: false,
    inputSchema: {
      type: 'object',
      properties: {
        input: { type: 'string' },
        format: { enum: ['text', 'markdown', 'json', 'csv'] },
        options: {
          type: 'object',
          properties: {
            csv_delimiter: { type: 'string' },
            json_indent: { type: 'number' },
            markdown_style: { enum: ['gfm', 'plain'] },
          },
        },
      },
      required: ['input', 'format'],
    },
    run: async (args) => formatOutput({
      input: inputToString(args.input),
      format: requireFormat(args.format),
      options: isRecord(args.options) ? args.options : undefined,
    }),
  },
]

export const graphToolRegistry = new Map(graphTools.map((tool) => [tool.name, tool]))

export function formatOutput(args: {
  input: string
  format: OutputFormat
  options?: FormatOutputOptions
}): FormatOutputResult {
  const formatted = formatOutputString(args.input, args.format, args.options ?? {})
  return {
    formatted,
    format: args.format,
    byteSize: new TextEncoder().encode(formatted).byteLength,
  }
}

function formatOutputString(input: string, format: OutputFormat, options: FormatOutputOptions): string {
  if (format === 'text') return input.trimEnd()
  if (format === 'markdown') return formatMarkdown(input, options.markdown_style ?? 'gfm')
  if (format === 'json') return formatJson(input, options.json_indent)
  return formatCsv(input, options.csv_delimiter)
}

function formatMarkdown(input: string, style: 'gfm' | 'plain'): string {
  const parsed = tryParseJson(input)
  if (style === 'gfm' && isObjectArray(parsed)) return objectArrayToMarkdownTable(parsed)

  const text = input.trim()
  if (!text) return ''
  if (looksLikeCodeBlock(text)) return `\`\`\`\n${text}\n\`\`\``

  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.split('\n').map((line) => line.trimEnd()).join('  \n'))
    .join('\n\n')
}

function formatJson(input: string, indentValue: unknown): string {
  const indent = clampInteger(Number(indentValue ?? 2), 0, 10)
  const parsed = tryParseJson(input)
  return JSON.stringify(parsed === undefined ? { output: input } : parsed, null, indent)
}

function formatCsv(input: string, delimiterValue: unknown): string {
  const parsed = tryParseJson(input)
  if (!isObjectArray(parsed)) throw new Error('CSV format requires JSON array of objects as input')

  const delimiter = typeof delimiterValue === 'string' && delimiterValue.length > 0 ? delimiterValue : ','
  const rows = parsed
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  const lines = [
    headers.map((header) => escapeCsvCell(header, delimiter)).join(delimiter),
    ...rows.map((row) => headers.map((header) => escapeCsvCell(csvValue(row[header]), delimiter)).join(delimiter)),
  ]
  return lines.join('\r\n')
}

function objectArrayToMarkdownTable(rows: Array<Record<string, unknown>>): string {
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row))))
  if (headers.length === 0) return ''

  return [
    `| ${headers.map(escapeMarkdownCell).join(' | ')} |`,
    `| ${headers.map(() => '---').join(' | ')} |`,
    ...rows.map((row) => `| ${headers.map((header) => escapeMarkdownCell(markdownValue(row[header]))).join(' | ')} |`),
  ].join('\n')
}

function escapeMarkdownCell(value: unknown): string {
  return String(value ?? '').replace(/\|/g, '\\|').replace(/\r?\n/g, '<br>')
}

function markdownValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function escapeCsvCell(value: string, delimiter: string): string {
  if (value.includes('"') || value.includes('\r') || value.includes('\n') || value.includes(delimiter)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function csvValue(value: unknown): string {
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function tryParseJson(input: string): unknown {
  try {
    return JSON.parse(input)
  } catch {
    return undefined
  }
}

function isObjectArray(value: unknown): value is Array<Record<string, unknown>> {
  return Array.isArray(value) && value.every(isRecord)
}

function looksLikeCodeBlock(input: string): boolean {
  const lines = input.split('\n')
  if (lines.some((line) => /^\s*```/.test(line))) return true
  if (lines.length < 2) return false
  const codeLikeLines = lines.filter((line) => /^\s*(const|let|var|function|class|import|export|if|for|while|return|{|}|<\/?[a-z][\s\S]*>|[.#]?[a-z0-9_-]+\s*{)/i.test(line))
  return codeLikeLines.length >= Math.min(2, lines.length)
}

function clampInteger(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min
  return Math.min(Math.max(Math.round(value), min), max)
}

function requireFormat(value: unknown): OutputFormat {
  if (value === 'text' || value === 'markdown' || value === 'json' || value === 'csv') return value
  throw new Error('format must be one of text, markdown, json, csv')
}

function parsePeriod(value: unknown): { start: string; end: string } {
  const today = new Date()
  const end = new Date(today)
  const start = new Date(today)

  if (value === 'yesterday') {
    start.setUTCDate(start.getUTCDate() - 1)
    end.setUTCDate(end.getUTCDate() - 1)
  } else if (value === 'last_7') {
    start.setUTCDate(start.getUTCDate() - 6)
  } else if (value === 'last_30') {
    start.setUTCDate(start.getUTCDate() - 29)
  } else if (value !== 'today' && value != null) {
    throw new Error(`Unsupported period: ${String(value)}`)
  }

  return { start: dateOnly(start), end: dateOnly(end) }
}

function parseReadMode(value: unknown): 'text' | 'json' | 'metadata_only' {
  if (value === 'text' || value === 'json' || value === 'metadata_only') return value
  throw new Error(`Unsupported read mode: ${String(value)}`)
}

function parseMaxBytes(value: unknown): number {
  const parsed = value == null ? DEFAULT_READ_MAX_BYTES : Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_READ_MAX_BYTES
  return Math.min(Math.trunc(parsed), HARD_READ_MAX_BYTES)
}

async function loadAttachment(ctx: GraphToolContext, attachmentId: string): Promise<ChatAttachmentRow> {
  const allowedIds = ctx.attachmentIds ?? []
  if (!allowedIds.includes(attachmentId)) {
    throw new AttachmentNotAllowedError()
  }

  const { data, error } = await (ctx.admin as any)
    .from('chat_attachments')
    .select('id, workspace_id, user_id, file_name, file_type, file_size, storage_path')
    .eq('id', attachmentId)
    .eq('workspace_id', ctx.workspaceId)
    .maybeSingle()

  if (error) throw new Error(`Failed to load attachment: ${error.message}`)
  if (!data) throw new AttachmentNotFoundError()
  return data as ChatAttachmentRow
}

async function downloadAttachmentBytes(ctx: GraphToolContext, storagePath: string): Promise<Buffer> {
  const { data, error } = await ctx.admin.storage
    .from(CHAT_ATTACHMENTS_BUCKET)
    .download(storagePath)
  if (error || !data) throw new Error(`Failed to download attachment: ${error?.message || 'missing file'}`)
  return Buffer.from(await data.arrayBuffer())
}

function attachmentResult(attachment: ChatAttachmentRow, content: string | null, truncated: boolean) {
  return {
    id: attachment.id,
    filename: attachment.file_name,
    mime_type: attachment.file_type,
    byte_size: Number(attachment.file_size ?? 0),
    content,
    truncated,
  }
}

function sanitizeStorageFilename(name: string): string {
  const cleaned = name
    .replace(/[^A-Za-z0-9.\-_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._]+|[._]+$/g, '')
  return cleaned || 'file'
}

function inferMimeType(filename: string): string {
  const ext = filename.toLowerCase().split('.').pop()
  if (ext === 'csv') return 'text/csv'
  if (ext === 'json') return 'application/json'
  if (ext === 'html') return 'text/html'
  if (ext === 'md') return 'text/markdown'
  if (ext === 'txt') return 'text/plain'
  return 'application/octet-stream'
}

async function loadCampaigns(ctx: GraphToolContext, campaignId: string | null): Promise<CampaignRow[]> {
  let query = (ctx.admin as any)
    .from('campaigns')
    .select('id, name, budget, start_date, end_date, form_state')
    .eq('workspace_id', ctx.workspaceId)
    .is('is_deleted', null)

  if (campaignId) query = query.eq('id', campaignId)

  const { data, error } = await query
  if (error) throw new Error(`Failed to load campaigns: ${error.message}`)
  return (data ?? []) as CampaignRow[]
}

async function loadPerformance(
  ctx: GraphToolContext,
  campaignIds: string[],
  period: { start: string; end: string },
): Promise<Map<string, { spend_cents: number; impressions: number; clicks: number }>> {
  const totals = new Map<string, { spend_cents: number; impressions: number; clicks: number }>()
  if (campaignIds.length === 0) return totals

  const { data, error } = await (ctx.admin as any)
    .from('supply_performance_daily')
    .select('campaign_id, spend_cents, impressions, clicks')
    .eq('workspace_id', ctx.workspaceId)
    .in('campaign_id', campaignIds)
    .gte('date', period.start)
    .lte('date', period.end)

  if (error) throw new Error(`Failed to load campaign performance: ${error.message}`)

  for (const row of (data ?? []) as PerfRow[]) {
    const existing = totals.get(row.campaign_id) ?? { spend_cents: 0, impressions: 0, clicks: 0 }
    existing.spend_cents += Number(row.spend_cents ?? 0)
    existing.impressions += Number(row.impressions ?? 0)
    existing.clicks += Number(row.clicks ?? 0)
    totals.set(row.campaign_id, existing)
  }

  return totals
}

function budgetCentsForCampaign(campaign: CampaignRow): number {
  const budget = Number(campaign.budget ?? 0)
  if (Number.isFinite(budget) && budget > 0) return Math.round(budget)

  const projections = projectionsFromFormState(campaign.form_state)
  return projections.reduce((sum, projection) => sum + projection.budget_cents, 0)
}

function dailyBudgetCentsForCampaign(campaign: CampaignRow): number {
  const projections = projectionsFromFormState(campaign.form_state)
  if (projections.length > 0) {
    return Math.round(projections.reduce((sum, projection) => sum + projection.daily_budget_cents, 0))
  }

  const totalBudget = budgetCentsForCampaign(campaign)
  const days = countInclusiveDays(campaign.start_date ?? '', campaign.end_date ?? '')
  return days > 0 ? Math.round(totalBudget / days) : totalBudget
}

function projectionsFromFormState(formState: unknown): Array<{ budget_cents: number; daily_budget_cents: number }> {
  if (!isRecord(formState) || !Array.isArray(formState.strategies)) return []

  return formState.strategies.flatMap((strategy) => {
    if (!isRecord(strategy) || !isRecord(strategy.budget)) return []
    const amount = Number(strategy.budget.adSpendBudget ?? 0)
    if (!Number.isFinite(amount) || amount <= 0) return []

    const startDate = typeof strategy.budget.startDate === 'string' ? strategy.budget.startDate : ''
    const endDate = typeof strategy.budget.endDate === 'string' && strategy.budget.endDate
      ? strategy.budget.endDate
      : ''
    const days = Math.max(1, countInclusiveDays(startDate, endDate))
    const budgetCents = strategy.budget.budgetType === 'daily'
      ? Math.round(amount * 100 * days)
      : Math.round(amount * 100)

    return [{
      budget_cents: budgetCents,
      daily_budget_cents: strategy.budget.budgetType === 'daily' ? Math.round(amount * 100) : Math.round(budgetCents / days),
    }]
  })
}

function countInclusiveDays(startDate: string, endDate: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return 0
  const start = new Date(`${startDate}T00:00:00.000Z`)
  const end = new Date(`${endDate}T00:00:00.000Z`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
}

function dateOnly(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function requireString(value: unknown, key: string): string {
  if (typeof value !== 'string' || !value) throw new Error(`${key} is required`)
  return value
}

function inputToString(value: unknown): string {
  if (typeof value === 'string') return value
  if (value == null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}
